import { useState, useRef } from 'react';

import { AppIcon, AppIconProps } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Menu, MenuItem, Typography, Box, Chip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

type DoubleSelectDropdownSide = 'left' | 'right';

export type DoubleSelectDropdownOption = Readonly<{
  value: string;
  label: string;
  dotColor?: AppIconProps['color'];
}>;

export type DoubleSelectDropdownProps = Readonly<{
  id?: string;
  sections: {
    key: DoubleSelectDropdownSide;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    options: DoubleSelectDropdownOption[];
    badgeCount?: number;
  }[];
}>;

export function DoubleSelectDropdown({ id, sections }: DoubleSelectDropdownProps) {
  const [menuAnchors, setMenuAnchors] = useState<
    Record<DoubleSelectDropdownSide, HTMLElement | null>
  >({
    left: null,
    right: null,
  });

  const buttonRefs: Record<DoubleSelectDropdownSide, React.RefObject<HTMLDivElement>> = {
    left: useRef(null),
    right: useRef(null),
  };

  const openMenu = (key: DoubleSelectDropdownSide) => {
    setMenuAnchors((prev) => ({
      ...prev,
      [key]: buttonRefs[key].current,
    }));
  };

  const closeMenu = (key: DoubleSelectDropdownSide) => {
    setMenuAnchors((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const leftSection = sections.find((s) => s.key === 'left');
  const rightSection = sections.find((s) => s.key === 'right');

  if (!leftSection || !rightSection) {
    return null;
  }

  return (
    <Box id={id} sx={containerStyles}>
      {/* Left Section */}
      <DropdownSection
        id={id ? `${id}-left` : undefined}
        value={leftSection.value}
        placeholder={leftSection.placeholder}
        options={leftSection.options}
        badgeCount={leftSection.badgeCount}
        onClick={() => openMenu('left')}
        buttonRef={buttonRefs.left}
        isLeft={true}
      />

      {/* Vertical divider for desktop */}
      <Box sx={dividerStyles} />

      {/* Horizontal divider for mobile */}
      <Box sx={horizontalDividerStyles} />

      {/* Right Section */}
      <DropdownSection
        id={id ? `${id}-right` : undefined}
        value={rightSection.value}
        placeholder={rightSection.placeholder}
        options={rightSection.options}
        badgeCount={rightSection.badgeCount}
        onClick={() => openMenu('right')}
        buttonRef={buttonRefs.right}
        isLeft={false}
      />

      {/* Left Menu */}
      <DropdownMenu
        id={id ? `${id}-left` : undefined}
        anchorEl={menuAnchors.left}
        isOpen={Boolean(menuAnchors.left)}
        onClose={() => closeMenu('left')}
        options={leftSection.options}
        onOptionClick={(value) => {
          leftSection.onChange(value);
          closeMenu('left');
        }}
        anchorHorizontal="left"
      />

      {/* Right Menu */}
      <DropdownMenu
        id={id ? `${id}-right` : undefined}
        anchorEl={menuAnchors.right}
        isOpen={Boolean(menuAnchors.right)}
        onClose={() => closeMenu('right')}
        options={rightSection.options}
        onOptionClick={(value) => {
          rightSection.onChange(value);
          closeMenu('right');
        }}
        anchorHorizontal="right"
      />
    </Box>
  );
}

// Custom select input section
type DropdownSectionProps = Readonly<{
  id?: string;
  value: string;
  placeholder: string;
  options: DoubleSelectDropdownOption[];
  badgeCount?: number;
  onClick: () => void;
  buttonRef: React.RefObject<HTMLDivElement>;
  isLeft?: boolean;
}>;

function DropdownSection({
  id,
  value,
  placeholder,
  options,
  badgeCount = 0,
  onClick,
  buttonRef,
  isLeft,
}: DropdownSectionProps) {
  const getDisplayText = () => {
    const selectedOption = options.find((option) => option.value === value);

    return selectedOption?.label || placeholder;
  };

  const displayText = getDisplayText();

  return (
    <Box
      id={id}
      ref={buttonRef}
      sx={isLeft ? leftSectionStyles : rightSectionStyles}
      onClick={onClick}
    >
      <Box sx={contentStyles}>
        <Typography
          variant="text-sm-semibold"
          color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-secondary']}
          sx={textTruncationStyles}
        >
          {displayText}
        </Typography>
        {isLeft && badgeCount > 0 && (
          <Chip
            label={badgeCount}
            size="small"
            variant="badge"
            type="leading"
            sx={chipSx}
            icon={<AppIcon iconName="Circle" diameter="8px" color="Brand/700" fill="Brand/700" />}
          />
        )}
        <AppIcon iconName="ChevronDown" diameter="16px" color="Colors/Text/text-primary" />
      </Box>
    </Box>
  );
}

// Custom paper dropdown menu when the select is clicked
type DropdownMenuProps = Readonly<{
  id?: string;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  options: DoubleSelectDropdownOption[];
  onOptionClick: (value: string) => void;
  anchorHorizontal: 'left' | 'right';
}>;

function DropdownMenu({
  id,
  anchorEl,
  isOpen,
  onClose,
  options,
  onOptionClick,
  anchorHorizontal,
}: DropdownMenuProps) {
  return (
    <Menu
      id={id ? `${id}-menu` : undefined}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: menuPaperStyles,
        },
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: anchorHorizontal,
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: anchorHorizontal,
      }}
    >
      {options.map((option) => (
        <MenuItem
          id={id ? `${id}-option-${option.value.toLowerCase().replace(/\s+/g, '-')}` : undefined}
          key={option.value}
          onClick={() => onOptionClick(option.value)}
          sx={menuItemStyles}
        >
          <Box sx={menuItemContentStyles}>
            <Typography variant="text-sm-regular">{option.label}</Typography>
            {option.dotColor && (
              <AppIcon
                iconName="Circle"
                diameter="8px"
                color={option.dotColor || 'Brand/700'}
                fill={option.dotColor || 'Brand/700'}
              />
            )}
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
}

const containerStyles: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  border: '1px solid',
  borderColor: backrs.colors['Colors/Foreground/fg-tertiary'],
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
  width: {
    xs: '100%',
    sm: 'fit-content',
  },
  cursor: 'pointer',
  minHeight: '40px',
  transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
  '&:hover': {
    borderColor: backrs.colors['Colors/Foreground/fg-primary'],
    boxShadow: backrs.shadows['shadow-lg'],
  },
  flexGrow: 1,
  '@media (max-width: 350px)': {
    flexDirection: 'column',
  },
});

const baseSectionStyles: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  mx: backrs.spacing['spacing-md'],
  padding: {
    xs: backrs.spacing['spacing-md'],
    sm: 0,
  },
  '&:hover': {
    backgroundColor: backrs.colors['Gray/50'],
  },
  transition: 'all 0.3s ease-in-out',
  width: '100%',
  '@container (max-width: 450px)': {
    minWidth: 0,
    overflow: 'hidden',
  },
});

const leftSectionStyles: SxProps<Theme> = (theme: Theme) => ({
  ...baseSectionStyles(theme),
  borderTopLeftRadius: theme.backrs.radius['radius-lg'],
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
});

const rightSectionStyles: SxProps<Theme> = (theme: Theme) => ({
  ...baseSectionStyles(theme),
  borderTopRightRadius: 0,
  borderBottomRightRadius: theme.backrs.radius['radius-lg'],
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
});

const contentStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: '8px',
};

const textTruncationStyles: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
};

const dividerStyles: SxProps<Theme> = ({ backrs }) => ({
  width: '1px',
  minHeight: '38px',
  backgroundColor: backrs.colors['Gray/300'],
  alignSelf: 'center',
  display: 'block',
  '@media (max-width: 350px)': {
    display: 'none',
  },
});

const horizontalDividerStyles: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: backrs.colors['Gray/300'],
  display: 'none',
  '@media (max-width: 350px)': {
    display: 'block',
  },
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-sm'],
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-sm']}`,
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    padding: `${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-xs']}`,
  },
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
});

const menuPaperStyles: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  minWidth: '200px',
  maxWidth: 'none',
});

const menuItemStyles: SxProps<Theme> = ({ backrs }) => ({
  padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']}`,
  '&.Mui-selected': {
    backgroundColor: backrs.colors['Brand/50'],
  },
  transition: 'all 0.3s ease-in-out',
  '&.MuiMenuItem-root': {
    '&:hover': {
      backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
    },
  },
});

const menuItemContentStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: '8px',
  paddingRight: '4px',
};
